import { useState } from "react";
import { formatDate, dayLabel } from "@/lib/constants";
import { EventReportButton, Stars, receiptUrl } from "@/components/EventReport";
import { ClipboardCheck, ChevronDown, FileText, CheckCircle2 } from "lucide-react";

export const pendingReportGroups = (groups, reports) =>
  groups.filter((g) => g[0].event_id && reports.ended.includes(g[0].event_id)
    && !reports.reports.find((x) => x.event_id === g[0].event_id && x.locked));

function SentReport({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4" data-testid={`tech-report-sent-${r.id}`}>
      <button className="w-full flex items-center gap-3 text-left" onClick={() => setOpen(!open)} data-testid={`tech-report-toggle-${r.id}`}>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{r.event_name}</p>
          <p className="text-xs text-slate-500">Inviato il {new Date(r.submitted_at).toLocaleString("it-IT", { timeZone: "Europe/Rome", dateStyle: "short", timeStyle: "short" })}</p>
        </div>
        <Stars value={r.rating} size="w-3.5 h-3.5" />
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-sm text-slate-300">
          <p><b>Orari:</b> {r.days.map((d) => `${dayLabel(d.date)} ${d.start || "–"}→${d.end || "–"}`).join(" · ") || "non indicati"}</p>
          <p className="whitespace-pre-wrap"><b>Problemi:</b> {r.problems || "nessuno"}</p>
          {r.notes && <p className="whitespace-pre-wrap"><b>Note:</b> {r.notes}</p>}
          <div><b>Spese:</b> {r.expenses.length ? <>{r.expenses.map((e, i) => <div key={i}>{e.description}: € {e.amount.toFixed(2)}</div>)}<div className="font-semibold">Totale € {r.expenses_total.toFixed(2)}</div></> : "nessuna"}</div>
          {r.receipts.length > 0 && <div className="flex flex-wrap gap-2">{r.receipts.map((x) => (
            <a key={x.id} href={receiptUrl(x)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs"><FileText className="w-3.5 h-3.5" />{x.filename}</a>))}</div>}
        </div>
      )}
    </div>
  );
}

export function TechReports({ groups, reports, onDone }) {
  const pending = pendingReportGroups(groups, reports);
  const sent = reports.reports.filter((r) => r.locked).sort((a, b) => (b.submitted_at || "").localeCompare(a.submitted_at || ""));
  return (
    <div className="space-y-5 animate-fade-up" data-testid="tech-reports">
      <div className="flex items-end justify-between gap-3">
        <h1 className="font-display text-2xl font-bold">Report</h1>
        <div data-testid="tech-report-count" className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-right">
          <p className="text-xl font-bold text-amber-300 leading-none">{reports.reports.length}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-400">report compilati</p>
        </div>
      </div>
      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-wider font-mono text-amber-300">Da inviare ({pending.length})</h2>
        {pending.length === 0 && <p className="text-sm text-slate-500 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Nessun report da inviare.</p>}
        {pending.map((g) => {
          const rep = reports.reports.find((x) => x.event_id === g[0].event_id);
          return (
            <div key={g[0].event_id} className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid={`tech-report-pending-${g[0].event_id}`}>
              <p className="font-semibold flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-amber-300" /> {g[0].event_name || "Evento"}</p>
              <p className="text-xs text-slate-400">{formatDate(g[0].date)}{g.length > 1 && ` → ${formatDate(g[g.length - 1].date)}`}{rep && " · sbloccato dal manager per correzioni"}</p>
              <EventReportButton group={g} report={rep} onDone={onDone} />
            </div>
          );
        })}
      </section>
      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-wider font-mono text-slate-500">Inviati ({sent.length})</h2>
        {sent.length === 0 && <p className="text-sm text-slate-500">Nessun report inviato.</p>}
        {sent.map((r) => <SentReport key={r.id} r={r} />)}
      </section>
    </div>
  );
}
